/**
 * Protocol Engine Schema
 *
 * Protocols are structured JSON objects — not PDFs, not flat text.
 * Each protocol is versioned, queryable, and carries billing codes
 * and narrative scaffolding metadata per step.
 */

export interface Protocol {
  id: string;
  name: string;
  category: ProtocolCategory;
  version: string;
  effectiveDate: string;         // YYYY-MM-DD
  source: string;                // e.g., "RI Statewide EMS Protocols and Standing Orders v2026.01"
  triggers: ProtocolTrigger[];
  steps: ProtocolStep[];
  alsLevel: 'BLS' | 'ALS-1' | 'ALS-2';
  pediatricOverlay: boolean;
  notes: string | null;
  /** Protocols that commonly run alongside this one — shown as "related" in UI */
  relatedProtocolIds?: string[];
  /** Fallback / consider-next protocols if initial protocol doesn't match */
  alternativeProtocolIds?: string[];
  /**
   * Condition-specific fields the RI protocols require to be documented
   * whenever this protocol is active. The validator enforces these
   * before finalization.
   */
  requirements?: ProtocolRequirement[];
}

/**
 * A compliance requirement declared by a protocol. When the protocol is
 * active, the field must be filled before the report can be finalized.
 * Stored on epcrs.compliance_fields[key].
 */
export interface ProtocolRequirement {
  key: string;                   // stable slug, e.g., "stroke_last_known_well"
  label: string;                 // human-readable question
  description?: string;          // clarifying help text
  type: 'datetime' | 'number' | 'text' | 'select' | 'boolean' | 'attachment' | 'reassessment';
  options?: string[];            // for type: select
  min?: number;
  max?: number;
  unit?: string;                 // display unit for numbers
  attachmentCategory?: string;   // for type: attachment (e.g., "12-lead-ecg")
  /** true = blocks finalization; false = best-practice but not blocking */
  required: boolean;
  /** Citation back to the protocol/section that mandates this field */
  cite: string;
  /** Show this requirement only if a specific decision was made (optional) */
  conditionalOn?: { stepOrder: number; choice: string };
}

export type ProtocolCategory =
  | 'cardiac'
  | 'respiratory'
  | 'neurological'
  | 'trauma'
  | 'medical'
  | 'ob'
  | 'environmental'
  | 'pediatric'
  | 'behavioral';

export interface ProtocolTrigger {
  type: 'chief_complaint' | 'dispatch_reason' | 'primary_impression' | 'vitals_threshold';
  value: string;
  /** For vitals_threshold: field name and comparison */
  vitalField?: string;
  vitalOperator?: '<' | '>' | '<=' | '>=';
  vitalValue?: number;
}

export interface ProtocolStep {
  order: number;
  description: string;
  /** Plain-language explanation for Guidance Mode */
  plainLanguage: string | null;
  type: 'assessment' | 'procedure' | 'medication' | 'decision' | 'monitor' | 'transport';
  /** If medication, dosing details */
  medication?: MedicationDose;
  /** If procedure, procedure type matching NEMSIS code */
  procedureType?: string;
  /** If decision, branch logic */
  decision?: DecisionNode;
  /** Billing codes auto-populated when step is completed */
  billing: BillingCode[];
  /** Required cert level to perform this step */
  requiredCert: 'EMT-B' | 'AEMT' | 'Paramedic' | null;
  /** Is this step time-sensitive? */
  timeSensitive: boolean;
  /** Contraindication logic */
  contraindications: string[];
  /**
   * Dependencies: this step only becomes active if:
   *   - All steps in `requiresStepsCompleted` are done, AND
   *   - All decision choices in `requiresDecision` match
   * If empty, step is always active.
   */
  requiresStepsCompleted?: number[];
  requiresDecision?: Array<{ stepOrder: number; choice: string }>;
}

export interface MedicationDose {
  drugName: string;
  adultDose: string;
  adultRoute: string;
  pediatricDose: string | null;    // Weight-based formula, e.g., "0.01 mg/kg"
  pediatricRoute: string | null;
  maxDose: string | null;
  repeatInterval: string | null;   // e.g., "5 minutes"
  maxRepeats: number | null;
}

export interface DecisionNode {
  question: string;
  /** Plain-language version for Guidance Mode */
  plainQuestion: string;
  yesAction: string;
  noAction: string;
  /** Vital sign field to check, if applicable */
  vitalCheck?: {
    field: string;
    operator: '<' | '>' | '<=' | '>=';
    value: number;
    unit: string;
  };
}

export interface BillingCode {
  type: 'HCPCS' | 'ICD-10' | 'ALS_LEVEL';
  code: string;
  description: string;
}

// ============================================================
// Protocol Execution State — per-ePCR runtime data
// ============================================================

/**
 * Runtime state for protocol execution on a single ePCR.
 * This IS the source of truth for which protocols were active,
 * which decisions were made, and which actions were completed.
 * Persisted to epcrs.protocol_state (JSONB column).
 */
export interface ProtocolExecutionState {
  /** All protocols that matched the incident's triggers (primary + related) */
  triggered_protocol_ids: string[];
  /** The primary protocol (user confirmed or auto-selected first match) */
  primary_protocol_id: string | null;
  /** Protocols shown as "related" — available to activate if clinically warranted */
  related_protocol_ids: string[];
  /** Protocols the user has explicitly activated (subset of triggered+related) */
  active_protocol_ids: string[];

  /**
   * Frozen version stamp per protocol, captured at the moment the
   * protocol activated (transitioned into `active_protocol_ids`).
   *
   * Legal chain-of-custody anchor: if a protocol is hot-fixed or the
   * annual update lands while a closed ePCR is later amended, the
   * stamp here resolves to the EXACT version that drove the call —
   * not the current code-version. Subpoena response queries this
   * field directly.
   *
   * Shape: `{ "ri-cardiac-acs": "2026.01", ... }`
   *
   * Never mutated after capture. The activation routine writes once
   * and refuses to overwrite a non-empty entry for the same id.
   */
  protocol_versions: Record<string, string>;

  /**
   * Effective dates (YYYY-MM-DD) corresponding to the version stamps
   * above. Captured alongside the version so a reader doesn't need
   * to look up the catalog to know the publication date.
   */
  protocol_effective_dates: Record<string, string>;

  /** Decisions made within protocol decision steps */
  decisions: ProtocolDecisionRecord[];

  /** Steps marked completed (either via direct checkbox or auto-linked to procedure/medication) */
  completed_steps: ProtocolStepCompletion[];

  /** Deviations — user documented care outside the protocol pathway */
  deviations: ProtocolDeviation[];

  /** When the user last re-evaluated protocol selection (for audit) */
  last_evaluated_at: string | null;
}

export interface ProtocolDecisionRecord {
  protocol_id: string;
  step_order: number;
  choice: string;          // 'yes', 'no', or other branch identifier
  decided_at: string;
  decided_by: string;      // user profile id
  rationale?: string;      // optional free-text context
}

export interface ProtocolStepCompletion {
  protocol_id: string;
  step_order: number;
  completed_at: string;
  completed_by: string;
  /** If this step was completed via a documented procedure/med, link to it */
  linked_procedure_id?: string;
  linked_medication_id?: string;
  /** Free-form note about how the step was completed */
  note?: string;
}

export interface ProtocolDeviation {
  protocol_id: string;
  step_order: number | null;   // null = general deviation from protocol
  reason: string;              // required clinical justification
  documented_at: string;
  documented_by: string;
  /** Optional: the unsanctioned action that was taken */
  action_type?: 'medication' | 'procedure' | 'omission';
  action_id?: string;          // FK to the procedure/medication record
}

export function emptyProtocolState(): ProtocolExecutionState {
  return {
    triggered_protocol_ids: [],
    primary_protocol_id: null,
    related_protocol_ids: [],
    active_protocol_ids: [],
    protocol_versions: {},
    protocol_effective_dates: {},
    decisions: [],
    completed_steps: [],
    deviations: [],
    last_evaluated_at: null,
  };
}

/**
 * Compute which steps are currently "active" (available to complete)
 * for a protocol, given the current execution state.
 * A step is active if its requires* conditions are all satisfied.
 */
export function activeStepsForProtocol(
  protocol: Protocol,
  state: ProtocolExecutionState,
): ProtocolStep[] {
  const decisionsByStep = new Map(
    state.decisions
      .filter((d) => d.protocol_id === protocol.id)
      .map((d) => [d.step_order, d.choice] as const),
  );
  const completedSet = new Set(
    state.completed_steps
      .filter((c) => c.protocol_id === protocol.id)
      .map((c) => c.step_order),
  );

  return protocol.steps.filter((step) => {
    if (step.requiresStepsCompleted?.length) {
      const allDone = step.requiresStepsCompleted.every((n) => completedSet.has(n));
      if (!allDone) return false;
    }
    if (step.requiresDecision?.length) {
      const allMet = step.requiresDecision.every(
        (r) => decisionsByStep.get(r.stepOrder) === r.choice,
      );
      if (!allMet) return false;
    }
    return true;
  });
}

/**
 * Collect all medications that are *permitted* by currently-active protocol steps.
 * Used to constrain the MedicationsTab formulary.
 */
export function permittedMedications(
  protocols: Protocol[],
  state: ProtocolExecutionState,
): { drugName: string; protocolId: string; stepOrder: number; dose: MedicationDose }[] {
  const out: { drugName: string; protocolId: string; stepOrder: number; dose: MedicationDose }[] = [];
  for (const protocolId of state.active_protocol_ids) {
    const protocol = protocols.find((p) => p.id === protocolId);
    if (!protocol) continue;
    const steps = activeStepsForProtocol(protocol, state);
    for (const step of steps) {
      if (step.type === 'medication' && step.medication) {
        out.push({
          drugName: step.medication.drugName,
          protocolId: protocol.id,
          stepOrder: step.order,
          dose: step.medication,
        });
      }
    }
  }
  return out;
}

/**
 * Collect all procedures permitted by active protocol steps.
 */
export function permittedProcedures(
  protocols: Protocol[],
  state: ProtocolExecutionState,
): { procedureType: string; protocolId: string; stepOrder: number }[] {
  const out: { procedureType: string; protocolId: string; stepOrder: number }[] = [];
  for (const protocolId of state.active_protocol_ids) {
    const protocol = protocols.find((p) => p.id === protocolId);
    if (!protocol) continue;
    const steps = activeStepsForProtocol(protocol, state);
    for (const step of steps) {
      if (step.type === 'procedure' && step.procedureType) {
        out.push({
          procedureType: step.procedureType,
          protocolId: protocol.id,
          stepOrder: step.order,
        });
      }
    }
  }
  return out;
}
